import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import * as categorizationService from "../service/categorization";
import { getRowDocumentId } from "../service/categorization";

const STATUS_IDLE = "idle";
const STATUS_LOADING = "loading";
const STATUS_SUCCEEDED = "succeeded";
const STATUS_FAILED = "failed";

const initialResource = () => ({
  byId: {},
  byDocumentId: {},
  status: STATUS_IDLE,
  error: null,
});

const initialState = {
  goldStandards: initialResource(),
  negativeExamples: initialResource(),
  categories: { list: [], subtypes: [], status: STATUS_IDLE, error: null },
  mutations: {},
};

const normalizeRow = (row) => {
  if (!row || typeof row !== "object") return null;
  return { ...row };
};

const indexRows = (rows = []) => {
  const byId = {};
  const byDocumentId = {};
  rows.forEach((row) => {
    const normalized = normalizeRow(row);
    if (!normalized) return;
    const id = String(normalized.id);
    byId[id] = normalized;
    const docId = getRowDocumentId(normalized);
    if (docId) byDocumentId[docId] = id;
  });
  return { byId, byDocumentId };
};

const mergeRow = (resource, row) => {
  const normalized = normalizeRow(row);
  if (!normalized || normalized.id == null) return;
  const id = String(normalized.id);
  resource.byId[id] = normalized;
  const docId = getRowDocumentId(normalized);
  if (docId) resource.byDocumentId[docId] = id;
};

const removeRow = (resource, id) => {
  if (id == null) return;
  const key = String(id);
  const existing = resource.byId[key];
  if (existing) {
    const docId = getRowDocumentId(existing);
    if (docId && resource.byDocumentId[docId] === key) {
      delete resource.byDocumentId[docId];
    }
  }
  delete resource.byId[key];
};

const extractErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return fallback || "Something went wrong.";
};

// ---- Thunks ----

export const fetchCategories = createAsyncThunk(
  "categorization/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const data = await categorizationService.fetchCategories();
      // The subtype vocabulary is served by the same endpoint. It is
      // absent on a backend predating subtypes, which must leave the
      // category dropdowns working exactly as before.
      return {
        categories: Array.isArray(data?.categories) ? data.categories : [],
        subtypes: Array.isArray(data?.subtypes) ? data.subtypes : [],
      };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Couldn't load categories."));
    }
  }
);

/**
 * Bulk-fetch the gold-standard + negative-example coverage for a set of
 * visible document IDs. Used by the matter document table to render
 * per-row badges in one pair of calls.
 */
export const fetchCategorizationForDocuments = createAsyncThunk(
  "categorization/fetchForDocuments",
  async (documentIds, { rejectWithValue }) => {
    try {
      return await categorizationService.fetchCategorizationForDocuments(documentIds);
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Couldn't load categorization library."));
    }
  }
);

export const createGoldStandardFromDocument = createAsyncThunk(
  "categorization/createGoldStandardFromDocument",
  async ({ documentId, category, notes }, { rejectWithValue }) => {
    try {
      const row = await categorizationService.createGoldStandardFromDocument({
        documentId,
        category,
        notes,
      });
      return row;
    } catch (err) {
      if (err?.response?.status === 409) {
        return rejectWithValue({
          status: 409,
          message: extractErrorMessage(
            err,
            "This document is already in the library for that category."
          ),
        });
      }
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(err, "Couldn't add to library — try again."),
      });
    }
  }
);

export const patchGoldStandard = createAsyncThunk(
  "categorization/patchGoldStandard",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await categorizationService.patchGoldStandard(id, data);
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(err, "Couldn't save changes — try again."),
      });
    }
  }
);

export const deleteGoldStandard = createAsyncThunk(
  "categorization/deleteGoldStandard",
  async (id, { rejectWithValue }) => {
    try {
      await categorizationService.deleteGoldStandard(id);
      return String(id);
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(err, "Couldn't delete entry — try again."),
      });
    }
  }
);

/**
 * Create a negative example with the BE's atomic paired-creation mode
 * (`also_add_gold_standard_for`). On success the slice receives both
 * rows together and updates both `goldStandards` and `negativeExamples`
 * caches in a single dispatch so the row badge can flip from
 * `gold-standard` → `reported` without an intermediate empty frame.
 */
export const createNegativeExampleWithCorrection = createAsyncThunk(
  "categorization/createNegativeExampleWithCorrection",
  async (
    { documentId, negativeForCategory, actualCategory, reason, notes },
    { rejectWithValue }
  ) => {
    try {
      const response = await categorizationService.createNegativeExample({
        documentId,
        negativeForCategory,
        reason,
        alsoAddGoldStandardFor: actualCategory,
        notes,
      });
      // Paired response shape per PR #512.
      if (response && response.negative_example && response.gold_standard) {
        return {
          negativeExample: response.negative_example,
          goldStandard: response.gold_standard,
        };
      }
      // Unpaired fallback — server returned just the negative example.
      return { negativeExample: response, goldStandard: null };
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(
          err,
          "Couldn't save the report — try again."
        ),
      });
    }
  }
);

export const recategorizeDocument = createAsyncThunk(
  "categorization/recategorizeDocument",
  async (
    { documentId, category, addNegative = false, reason = "" },
    { rejectWithValue }
  ) => {
    try {
      return await categorizationService.recategorizeDocument({
        documentId,
        category,
        addNegative,
        reason,
      });
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(err, "Couldn't recategorize — try again."),
      });
    }
  }
);

export const patchNegativeExample = createAsyncThunk(
  "categorization/patchNegativeExample",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await categorizationService.patchNegativeExample(id, data);
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(err, "Couldn't save changes — try again."),
      });
    }
  }
);

export const deleteNegativeExample = createAsyncThunk(
  "categorization/deleteNegativeExample",
  async (id, { rejectWithValue }) => {
    try {
      await categorizationService.deleteNegativeExample(id);
      return String(id);
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status,
        message: extractErrorMessage(err, "Couldn't delete entry — try again."),
      });
    }
  }
);

// ---- Slice ----

const slice = createSlice({
  name: "categorization",
  initialState,
  reducers: {
    // Reverse of an optimistic add — used by the "Undo" toast after a
    // right-click "Add to gold standard library" lands.
    rollbackGoldStandard(state, action) {
      removeRow(state.goldStandards, action.payload);
    },
    // Local-only cache invalidation, e.g. on logout / matter switch.
    clearCategorizationCache(state) {
      state.goldStandards = initialResource();
      state.negativeExamples = initialResource();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.categories.status = STATUS_LOADING;
        state.categories.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories.status = STATUS_SUCCEEDED;
        // Tolerate the pre-subtype payload (a bare array) so a stale
        // cached thunk result can't blank out the dropdowns.
        const payload = action.payload;
        state.categories.list = Array.isArray(payload)
          ? payload
          : payload?.categories || [];
        state.categories.subtypes = Array.isArray(payload)
          ? []
          : payload?.subtypes || [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categories.status = STATUS_FAILED;
        state.categories.error = action.payload || "Couldn't load categories.";
      })
      .addCase(fetchCategorizationForDocuments.pending, (state) => {
        state.goldStandards.status = STATUS_LOADING;
        state.negativeExamples.status = STATUS_LOADING;
      })
      .addCase(fetchCategorizationForDocuments.fulfilled, (state, action) => {
        const { goldStandards = [], negativeExamples = [] } = action.payload || {};
        const gold = indexRows(goldStandards);
        const neg = indexRows(negativeExamples);
        // Merge instead of replace — multiple matters can stack rows
        // into the same cache across navigation.
        Object.assign(state.goldStandards.byId, gold.byId);
        Object.assign(state.goldStandards.byDocumentId, gold.byDocumentId);
        Object.assign(state.negativeExamples.byId, neg.byId);
        Object.assign(state.negativeExamples.byDocumentId, neg.byDocumentId);
        state.goldStandards.status = STATUS_SUCCEEDED;
        state.negativeExamples.status = STATUS_SUCCEEDED;
        state.goldStandards.error = null;
        state.negativeExamples.error = null;
      })
      .addCase(fetchCategorizationForDocuments.rejected, (state, action) => {
        state.goldStandards.status = STATUS_FAILED;
        state.negativeExamples.status = STATUS_FAILED;
        state.goldStandards.error = action.payload || "Couldn't load library.";
        state.negativeExamples.error = action.payload || "Couldn't load library.";
      })
      .addCase(createGoldStandardFromDocument.fulfilled, (state, action) => {
        mergeRow(state.goldStandards, action.payload);
      })
      .addCase(patchGoldStandard.fulfilled, (state, action) => {
        mergeRow(state.goldStandards, action.payload);
      })
      .addCase(deleteGoldStandard.fulfilled, (state, action) => {
        removeRow(state.goldStandards, action.payload);
      })
      .addCase(createNegativeExampleWithCorrection.fulfilled, (state, action) => {
        const { negativeExample, goldStandard } = action.payload || {};
        if (negativeExample) mergeRow(state.negativeExamples, negativeExample);
        if (goldStandard) mergeRow(state.goldStandards, goldStandard);
      })
      .addCase(patchNegativeExample.fulfilled, (state, action) => {
        mergeRow(state.negativeExamples, action.payload);
      })
      .addCase(deleteNegativeExample.fulfilled, (state, action) => {
        const id = action.payload;
        // The BE deletes the paired GoldStandard in the same transaction.
        // Mirror that on the client so the row badge clears immediately.
        const negativeRow = state.negativeExamples.byId[id];
        if (negativeRow) {
          const docId = getRowDocumentId(negativeRow);
          if (docId) {
            const pairedGoldId = state.goldStandards.byDocumentId[docId];
            if (pairedGoldId) removeRow(state.goldStandards, pairedGoldId);
          }
        }
        removeRow(state.negativeExamples, id);
      });
  },
});

export const { rollbackGoldStandard, clearCategorizationCache } = slice.actions;

export default slice.reducer;

// ---- Selectors ----

const FROZEN_EMPTY_FLAGS_MAP = Object.freeze({});

const selectCategorizationSlice = (state) => state?.categorization;

export const selectCategorizationCategories = createSelector(
  [selectCategorizationSlice],
  (slice) => slice?.categories?.list || []
);

const FROZEN_EMPTY_LIST = Object.freeze([]);

/**
 * The subtype vocabulary, as served by the backend. Empty is a normal
 * answer, not a failure: it means this backend has no subtypes, and
 * every subtype control hides itself rather than offering an empty
 * dropdown.
 */
export const selectCategorizationSubtypes = createSelector(
  [selectCategorizationSlice],
  (slice) => slice?.categories?.subtypes || FROZEN_EMPTY_LIST
);

export const selectCategoriesStatus = (state) =>
  state?.categorization?.categories?.status || STATUS_IDLE;

export const selectGoldStandardForDocument = (state, docId) => {
  if (!state?.categorization || docId == null) return null;
  const key = String(docId);
  const id = state.categorization.goldStandards.byDocumentId[key];
  return id ? state.categorization.goldStandards.byId[id] || null : null;
};

export const selectNegativeExampleForDocument = (state, docId) => {
  if (!state?.categorization || docId == null) return null;
  const key = String(docId);
  const id = state.categorization.negativeExamples.byDocumentId[key];
  return id ? state.categorization.negativeExamples.byId[id] || null : null;
};

const docIdsKey = (docIds) =>
  Array.isArray(docIds)
    ? Array.from(new Set(docIds.map(String).filter(Boolean)))
        .sort()
        .join(",")
    : "";

/**
 * Reselect-cached map of { docId → { hasGoldStandard, hasNegativeExample,
 * goldStandardId, negativeExampleId } } for the supplied id list. The
 * cache key is the sorted, deduped CSV of the input ids so the same
 * reference is returned for the same set even if the caller's array
 * identity changes between renders. Matches the pattern used by
 * `selectRunningAnalysisDocIds` in `AISlice`.
 */
const flagsSelectorCache = new Map();

export const selectCategorizationFlagsForDocuments = (state, docIds) => {
  if (!Array.isArray(docIds) || docIds.length === 0) {
    return FROZEN_EMPTY_FLAGS_MAP;
  }
  const slice = state?.categorization;
  if (!slice) return FROZEN_EMPTY_FLAGS_MAP;

  const cacheKey = docIdsKey(docIds);
  let entry = flagsSelectorCache.get(cacheKey);
  if (!entry) {
    entry = {
      lastInputs: null,
      lastResult: FROZEN_EMPTY_FLAGS_MAP,
    };
    flagsSelectorCache.set(cacheKey, entry);
  }

  const goldByDoc = slice.goldStandards.byDocumentId;
  const goldById = slice.goldStandards.byId;
  const negByDoc = slice.negativeExamples.byDocumentId;
  const negById = slice.negativeExamples.byId;

  if (
    entry.lastInputs &&
    entry.lastInputs.goldByDoc === goldByDoc &&
    entry.lastInputs.goldById === goldById &&
    entry.lastInputs.negByDoc === negByDoc &&
    entry.lastInputs.negById === negById
  ) {
    return entry.lastResult;
  }

  const next = {};
  Array.from(new Set(docIds.map(String).filter(Boolean))).forEach((docId) => {
    const goldId = goldByDoc[docId];
    const negId = negByDoc[docId];
    if (!goldId && !negId) return;
    next[docId] = {
      hasGoldStandard: Boolean(goldId && goldById[goldId]),
      hasNegativeExample: Boolean(negId && negById[negId]),
      goldStandardId: goldId || null,
      negativeExampleId: negId || null,
    };
  });

  const frozen = Object.freeze(next);
  entry.lastInputs = { goldByDoc, goldById, negByDoc, negById };
  entry.lastResult = frozen;
  return frozen;
};

// Test-only helper to keep the per-input-set cache from leaking across
// jest test files. Exported under a `_internals` namespace so production
// code can't accidentally reach for it.
export const _internals = {
  resetFlagsCache: () => flagsSelectorCache.clear(),
  extractErrorMessage,
  STATUS_IDLE,
  STATUS_LOADING,
  STATUS_SUCCEEDED,
  STATUS_FAILED,
};
